import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  BASE_PERMISSIONS,
  BASE_ROLES,
  BaseAuthGuard,
  BaseErrorComponent,
  BaseProfileGuard,
  BaseAuthorisedUserGuard,
} from 'base';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { WrapperComponent } from './layout/wrapper/wrapper.component';
import { GrowthPspDataGuard } from './modules/shared/_guards/growth-psp-data.guard';

const routes: Routes = [
  // Public routes
  { path: '', redirectTo: 'auth', pathMatch: 'full' },
  {
    path: 'auth',
    canMatch: [BaseAuthorisedUserGuard],
    loadChildren: () => import('./modules/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: '401',
    component: BaseErrorComponent,
    data: {
      title: 'systemPage.401.headText',
      is401: true,
      isErrorPage: true,
    },
  },
  {
    path: '403',
    component: BaseErrorComponent,
    data: {
      title: 'systemPage.403.headText',
      is403: true,
      isErrorPage: true,
    },
  },
  {
    path: '404',
    component: BaseErrorComponent,
    data: {
      title: 'systemPage.404.headText',
      is404: true,
      isErrorPage: true,
    },
  },

  // Protected routes
  {
    path: '',
    component: WrapperComponent,
    canMatch: [BaseAuthGuard],
    canActivate: [BaseProfileGuard, GrowthPspDataGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: 'administration',
        loadChildren: () =>
          import('./modules/administration/administration.module').then((m) => m.AdministrationModule),
      },
      {
        path: 'employees',
        canMatch: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [BASE_ROLES.PSP_ADMIN, BASE_PERMISSIONS.FEATURE_ADMIN_PROFILES],
            redirectTo: '403',
          },
        },
        loadChildren: () => import('./modules/candidates/candidates.module').then((m) => m.CandidatesModule),
      },
      {
        path: 'inquiries',
        loadChildren: () => import('./modules/inquiry/inquiry.module').then((m) => m.InquiryModule),
      },
      {
        path: 'customer-locations',
        loadChildren: () =>
          import('./modules/related-customer-locations/related-customer-locations.module').then(
            (m) => m.RelatedCustomerLocationsModule
          ),
      },
      {
        path: 'assignments',
        canMatch: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [BASE_ROLES.PSP_ADMIN, BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS],
            redirectTo: '403',
          },
        },
        loadChildren: () => import('./modules/operations/operations.module').then((m) => m.OperationsModule),
      },
      {
        // KS-4217: added redirects to preserve compatibility with legacy routes.
        path: 'operations',
        redirectTo: '/assignments',
        pathMatch: 'full',
      },
      {
        path: 'offers-and-bookings',
        loadChildren: () =>
          import('./modules/offers-and-bookings/offers-and-bookings.module').then((m) => m.OffersAndBookingsModule),
      },
      {
        path: 'time-recordings',
        canMatch: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              BASE_ROLES.PSP_ADMIN,
              BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS,
              BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENT_DATES,
            ],
            redirectTo: '403',
          },
        },
        loadChildren: () =>
          import('./modules/time-recordings/time-recordings.module').then((m) => m.TimeRecordingsModule),
      },
      {
        path: 'planning-board',
        loadChildren: () => import('./modules/planning-board/planning-board.module').then((m) => m.PlanningBoardModule),
      },
      {
        path: 'absence-requests',
        canMatch: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [BASE_ROLES.PSP_ADMIN, BASE_PERMISSIONS.FEATURE_ADMIN_PROFILES],
            redirectTo: '403',
          },
        },
        loadChildren: () =>
          import('./modules/absence-requests/absence-requests.module').then((m) => m.AbsenceRequestsModule),
      },
    ],
  },
  { path: '**', redirectTo: '404' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
